// @ts-check

/** @file Provides a duration input wired to Api Maker forms. */

import {useForm} from "@kaspernj/api-maker/build/form"
import PropTypes from "prop-types"
import PropTypesExact from "prop-types-exact"
import {memo, useEffect} from "react"
import {ShapeComponent, shapeComponent} from "set-state-compare/build/shape-component.js"

import DurationInput from "../duration-input"

/** @import {NamedExoticComponent, ReactNode} from "react" */

/**
 * @typedef {object} FormDurationInputProps
 * @property {string} [defaultValue] Initial input value.
 * @property {boolean} [disabled] Whether the field should reject user edits and keyboard submits.
 * @property {string} [errorText] External validation error to show below the input.
 * @property {string} label Field label.
 * @property {string} name Form field name.
 * @property {string} [placeholder] Placeholder text.
 * @property {boolean} [submitOnEnter] Whether Enter submits the surrounding form.
 * @property {string} testID Stable test id.
 */

/** @typedef {Record<string, never>} FormDurationInputState */

/**
 * Form-connected duration input.
 * @augments {ShapeComponent<FormDurationInputProps, FormDurationInputState>}
 */
class FormDurationInput extends ShapeComponent {
  static propTypes = PropTypesExact({
    defaultValue: PropTypes.string,
    disabled: PropTypes.bool,
    errorText: PropTypes.string,
    label: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
    placeholder: PropTypes.string,
    submitOnEnter: PropTypes.bool,
    testID: PropTypes.string.isRequired
  })
  static defaultProps = {
    defaultValue: undefined,
    disabled: false,
    errorText: undefined,
    placeholder: undefined,
    submitOnEnter: true
  }

  /** @type {ReturnType<typeof useForm> | null} */
  form = null

  /** Registers the field with the surrounding form. */
  setup() {
    this.form = useForm()
    const defaultValue = this.p.defaultValue
    const fieldName = this.p.name

    useEffect(() => {
      if (this.form && defaultValue !== undefined) {
        this.form.setValue(fieldName, defaultValue)
      }
    }, [defaultValue, fieldName])
  }

  /**
   * Renders the form-connected duration field.
   * @returns {ReactNode} Duration input content.
   */
  render() {
    return (
      <DurationInput
        defaultValue={this.p.defaultValue}
        disabled={this.p.disabled}
        errorText={this.p.errorText}
        label={this.p.label}
        onChangeText={this.tt.onChangeText}
        onEndEditing={this.tt.onEndEditing}
        onSubmitEditing={!this.p.disabled && this.p.submitOnEnter ? this.tt.onSubmitEditing : undefined}
        placeholder={this.p.placeholder}
        testID={this.p.testID}
      />
    )
  }

  /**
   * Writes a text value into the form.
   * @param {string} value Text value to store.
   * @returns {void} Nothing.
   */
  setFormValue(value) {
    if (this.form) this.form.setValue(this.p.name, value)
  }

  /**
   * Handles text changes from React Native TextInput.
   * @param {string} value Text value to store.
   * @returns {void} Nothing.
   */
  onChangeText = (value) => {
    this.setFormValue(value)
  }

  /**
   * Syncs the committed native text value.
   * @param {{nativeEvent?: {text?: string}}} event Native event carrying committed text.
   * @returns {void} Nothing.
   */
  onEndEditing = (event) => {
    this.setFormValue(event.nativeEvent?.text || "")
  }

  /**
   * Syncs the submitted text and submits the surrounding form.
   * @param {{nativeEvent?: {text?: string}}} event Native text event.
   * @returns {void} Nothing.
   * @throws {Error} When no surrounding Api Maker Form is registered.
   */
  onSubmitEditing = (event) => {
    if (this.p.disabled) {
      // Disabled fields mirror disabled submit controls and ignore keyboard submits.
      return
    }

    if (!this.form) throw new Error("FormDurationInput requires an Api Maker Form for submitOnEnter")

    const submittedText = event.nativeEvent?.text
    this.form.setValue(
      this.p.name,
      submittedText !== undefined ? submittedText : this.form.getValue(this.p.name) || ""
    )
    this.form.submit()
  }
}

/** @type {NamedExoticComponent<FormDurationInputProps>} */
const FormDurationInputComponent = memo(shapeComponent(FormDurationInput))

export default FormDurationInputComponent
